import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(sessionStorage.getItem('admin_token'));
  const [loading, setLoading] = useState(true);
  const [mfaChallenge, setMfaChallenge] = useState(null);

  const adminApiRef = useRef(null);

  if (!adminApiRef.current) {
    adminApiRef.current = axios.create({
      baseURL: `${API_URL}/api/admin`,
      headers: { 'Content-Type': 'application/json' },
    });

    adminApiRef.current.interceptors.request.use((config) => {
      const currentToken = sessionStorage.getItem('admin_token');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    });
  }

  const adminApi = adminApiRef.current;

  // Handle 401 responses
  useEffect(() => {
    const interceptorId = adminApi.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && sessionStorage.getItem('admin_token')) {
          sessionStorage.removeItem('admin_token');
          setToken(null);
          setAdmin(null);
        }
        return Promise.reject(error);
      }
    );
    return () => adminApi.interceptors.response.eject(interceptorId);
  }, [adminApi]);

  // Check if admin is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = sessionStorage.getItem('admin_token');
      if (storedToken) {
        try {
          const response = await adminApi.get('/auth/me');
          setAdmin(response.data);
          setToken(storedToken);
        } catch (error) {
          sessionStorage.removeItem('admin_token');
          setToken(null);
          setAdmin(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [adminApi]);

  const login = useCallback(async (email, password, mfaCode) => {
    const payload = { email, password };
    if (mfaCode) payload.mfa_code = mfaCode;

    const response = await adminApi.post('/auth/login', payload);
    const data = response.data;

    // Backend returns requires_mfa when MFA is enabled and no code provided
    if (data.requires_mfa) {
      setMfaChallenge({ adminId: data.admin_id, email, password });
      return { requiresMfa: true };
    }

    // Full login success
    const { access_token, admin: adminData } = data;
    sessionStorage.setItem('admin_token', access_token);
    setToken(access_token);
    setAdmin(adminData);
    setMfaChallenge(null);
    return adminData;
  }, [adminApi]);

  const completeMfaLogin = useCallback(async (mfaCode) => {
    if (!mfaChallenge) throw new Error('No MFA challenge pending');
    const result = await login(mfaChallenge.email, mfaChallenge.password, mfaCode);
    return result;
  }, [mfaChallenge, login]);

  const cancelMfa = useCallback(() => {
    setMfaChallenge(null);
  }, []);

  const logout = useCallback(async () => {
    try {
      await adminApi.post('/auth/logout');
    } catch (error) {
      // Ignore logout errors
    }
    sessionStorage.removeItem('admin_token');
    setToken(null);
    setAdmin(null);
  }, [adminApi]);

  const hasPermission = useCallback((permission) => {
    if (!admin) return false;
    return admin.permissions?.includes(permission) || false;
  }, [admin]);

  const value = {
    admin,
    token,
    loading,
    isAuthenticated: !!admin,
    mfaChallenge,
    login,
    completeMfaLogin,
    cancelMfa,
    logout,
    hasPermission,
    adminApi,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};
